import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppContext } from "../../../providers/ProviderAppContainer";
import Loading from "../../common/Loading";
import NoContent from "../../common/NoContent";
import TabHeader from "../../common/TabHeader";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { FloatLabel } from "primereact/floatlabel";
import OrderManager from "../../common/OrderManager";
import HasRequiredAuthority from "../../dependencies/HasRequiredAuthority";
import { AUTHORITIES } from "../../../constants";
import { TEXT_SMALL } from "../../../style";
import CoursePromoDialogContent from "./CoursePromoDialogContent";
import DialogAddCoursePromoDialogContent from "./DialogAddCoursePromoDialogContent";
import DialogEditCoursePromoDialogContent from "./DialogEditCoursePromoDialogContent";

export default function CoursePromoDialogsTab() {
    const { requestAPI, showToast } = useAppContext();

    const [courses, setCourses] = useState();
    const [loading, setLoading] = useState();
    const [loadingContents, setLoadingContents] = useState();
    const [error, setError] = useState();
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [contents, setContents] = useState([]);
    const [updatingViewIndex, setUpdatingViewIndex] = useState();
    const [savingViewIndex, setSavingViewIndex] = useState();
    const [dialogAddCoursePromoDialogContent, setDialogAddCoursePromoDialogContent] = useState({ visible: false });
    const [dialogEditCoursePromoDialogContent, setDialogEditCoursePromoDialogContent] = useState({ visible: false });

    const courseOptions = useMemo(
        () =>
            (courses ?? []).map((course) => ({
                ...course,
                label: course.category_title ? `${course.category_title} — ${course.title}` : course.title,
            })),
        [courses],
    );

    const closeDialogAddCoursePromoDialogContent = useCallback(() => {
        setDialogAddCoursePromoDialogContent((prev) => ({ ...prev, visible: false }));
    }, []);

    const closeDialogEditCoursePromoDialogContent = useCallback(() => {
        setDialogEditCoursePromoDialogContent((prev) => ({ ...prev, visible: false }));
    }, []);

    const loadCourseContents = useCallback(
        (courseId) => {
            if (!courseId) {
                setContents([]);
                return;
            }

            requestAPI({
                requestPath: `dialog-contents/courses/${courseId}/contents`,
                requestMethod: "GET",
                setLoading: setLoadingContents,
                onResponseReceieved: (contentsResponse, responseCode) => {
                    if (responseCode === 200 && Array.isArray(contentsResponse)) {
                        setContents(contentsResponse);
                    } else {
                        setContents([]);
                        showToast({ severity: "error", summary: "Failed", detail: "Couldn't load promo contents", life: 2000 });
                    }
                },
            });
        },
        [requestAPI, showToast],
    );

    useEffect(() => {
        requestAPI({
            requestPath: "dialog-contents/courses",
            requestMethod: "GET",
            onRequestStart: () => setError(),
            setLoading: setLoading,
            onRequestFailure: setError,
            onResponseReceieved: (coursesResponse, responseCode) => {
                if (Array.isArray(coursesResponse) && responseCode === 200) {
                    setCourses(coursesResponse);
                } else {
                    setError("Couldn't load promo dialog courses");
                }
            },
        });
    }, [requestAPI]);

    const onCourseChange = useCallback(
        (course) => {
            setSelectedCourse(course);
            setUpdatingViewIndex(false);
            loadCourseContents(course?.id);
        },
        [loadCourseContents],
    );

    const updateViewIndexes = useCallback(() => {
        requestAPI({
            requestPath: `dialog-contents/courses/${selectedCourse.id}/view_indexes`,
            requestMethod: "PATCH",
            requestPostBody: contents.map(({ id }, view_index) => ({ id, view_index })),
            setLoading: setSavingViewIndex,
            parseResponseBody: false,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Update View Indexes !", life: 2000 }),
            onResponseReceieved: (_, responseCode) => {
                if (responseCode === 200) {
                    const nextContents = contents.map((content, view_index) => ({ ...content, view_index }));
                    setContents(nextContents);
                    showToast({ severity: "success", summary: "Updated", detail: "View Indexes Updated", life: 1000 });
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: "Failed To Update View Indexes !", life: 2000 });
                }
            },
        });
    }, [contents, requestAPI, selectedCourse?.id, showToast]);

    if (loading && !courses) {
        return <Loading message="Loading promo dialogs" />;
    }

    if (error) {
        return <NoContent error={error} />;
    }

    return (
        <div className="flex flex-column h-full flex-1">
            <TabHeader
                className="px-3 pt-3"
                title="Course Promo Dialogs"
                highlights={["Select a course to manage its promo slides"]}
                actionItems={[
                    selectedCourse ? (
                        <HasRequiredAuthority requiredAuthority={AUTHORITIES.UPDATE_COURSE}>
                            <Button
                                icon="pi pi-plus"
                                severity="warning"
                                onClick={() =>
                                    setDialogAddCoursePromoDialogContent((prev) => ({
                                        ...prev,
                                        visible: true,
                                        courseId: selectedCourse.id,
                                    }))
                                }
                            />
                        </HasRequiredAuthority>
                    ) : null,
                    selectedCourse && !!contents?.length ? (
                        <HasRequiredAuthority requiredAuthority={AUTHORITIES.UPDATE_COURSE}>
                            <Button
                                loading={savingViewIndex}
                                onClick={() => {
                                    showToast({
                                        severity: "info",
                                        summary: "Repositioning",
                                        detail: `Repositioning Mode ${!updatingViewIndex ? "Enabled" : "Disabled"}`,
                                        life: 1000,
                                    });
                                    if (updatingViewIndex) {
                                        updateViewIndexes();
                                    }
                                    setUpdatingViewIndex((prev) => !prev);
                                }}
                                icon="pi pi-arrows-v"
                            />
                        </HasRequiredAuthority>
                    ) : null,
                ].filter(Boolean)}
            />

            <FloatLabel className="px-3 mt-4 w-full">
                <Dropdown
                    value={selectedCourse}
                    inputId="promo-course"
                    options={courseOptions}
                    optionLabel="label"
                    filter
                    filterBy="label,title,category_title"
                    className="w-full"
                    onChange={(e) => onCourseChange(e.value)}
                    pt={{
                        label: { className: TEXT_SMALL },
                        item: { className: TEXT_SMALL },
                    }}
                />
                <label htmlFor="promo-course" className={TEXT_SMALL}>
                    Select Course
                </label>
            </FloatLabel>

            <div className="flex-1 min-h-0 flex flex-column mt-3">
                {!selectedCourse ? (
                    <NoContent error="Select a course to manage promo dialog content" />
                ) : loadingContents ? (
                    <Loading message="Loading promo contents" />
                ) : (
                    <OrderManager
                        updatingViewIndex={updatingViewIndex}
                        items={contents}
                        setItems={setContents}
                        entity="Promo Contents"
                        itemTemplate={(item) => (
                            <CoursePromoDialogContent
                                {...item}
                                updatingViewIndex={updatingViewIndex}
                                setContents={setContents}
                                setDialogEditCoursePromoDialogContent={setDialogEditCoursePromoDialogContent}
                            />
                        )}
                    />
                )}
            </div>

            {dialogAddCoursePromoDialogContent?.visible && (
                <DialogAddCoursePromoDialogContent
                    {...dialogAddCoursePromoDialogContent}
                    closeDialog={closeDialogAddCoursePromoDialogContent}
                    setContents={setContents}
                />
            )}
            {dialogEditCoursePromoDialogContent?.visible && (
                <DialogEditCoursePromoDialogContent
                    closeDialog={closeDialogEditCoursePromoDialogContent}
                    setContents={setContents}
                    {...dialogEditCoursePromoDialogContent}
                />
            )}
        </div>
    );
}
